import useLocalStorage from '@rehooks/local-storage'
import { useAcessoLotacao } from 'components/auth/useAcessoLotacao'
import useSession from 'components/auth/useSession'
import { Breadcrumb } from 'components/breadcrumb'
import { PageLoading } from 'components/loading'
import { PecSwitch } from 'components/route'
import { useAgendamentosDiaQuery, useAtendimentoVacinacaoViewQuery } from 'graphql/hooks.generated'
import { SituacaoAgendadoEnum, StatusAtendimento, TipoAtendimentoProfissional } from 'graphql/types.generated'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useRouteMatch } from 'react-router'
import { Redirect, Route } from 'react-router-dom'

import Permissions from '../../../types/Permissions'
import { AtendimentoHeader } from '../detail/AtendimentoHeader'
import { CidadaoDetailView } from '../detail/cadastro-cidadao/CidadaoDetailView'
import { VacinacaoView } from '../detail/vacinacao/VacinacaoView'
import { AtendimentoVacinacaoState, initialValue } from './model'

interface AtendimentoVacinacaoViewProps {
  atendimentoId: ID
}

export function AtendimentoVacinacaoView({ atendimentoId }: AtendimentoVacinacaoViewProps) {
  const { data: sessao, hasAuthorization, isUnidadeSaudeCEO } = useSession()
  const match = useRouteMatch()
  const acesso = useAcessoLotacao()

  const {
    data: { atendimento },
    loading: loadingAtendimento,
  } = useAtendimentoVacinacaoViewQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      atendimentoId,
    },
  })

  const { unidadeSaude } = useAcessoLotacao()
  const { cidadao, prontuario, atendimentoProfissional } = atendimento || {}

  const [cacheState, setCacheState, deleteCacheState] = useLocalStorage<AtendimentoVacinacaoState>(
    `${sessao.acesso.id}/atendimento/vacinacao/${atendimentoId}`
  )

  const canAccess = hasAuthorization(Permissions.visualizarListaDeAtendimento.cadastrarEditarEExcluir)

  const [initialStateLoaded, setInitialStateLoaded] = useState(!!cacheState)

  const today = moment().startOf('day').toDate()
  const { data: agendamentosDiaQueryResults, loading: loadingAgendamento } = useAgendamentosDiaQuery({
    variables: {
      input: {
        cidadaoId: cidadao?.id,
        dataAgendadoInicio: today,
        dataAgendadoFim: today,
        situacao: [SituacaoAgendadoEnum.AGENDADO, SituacaoAgendadoEnum.CIDADAO_PRESENTE_NA_UNIDADE],
        unidadeSaudeId: unidadeSaude.id,
        isForaDaUbs: false,
        isOrigemAtencaoDomiciliar: false,
      },
    },
    skip: !cidadao?.id,
  })
  const agendamentosDia = agendamentosDiaQueryResults?.agendados?.content?.filter(
    (agendado) => agendado.id !== atendimento.agendado?.id || agendado?.lotacaoAgendada?.id !== sessao.acesso.id
  )

  const isLoading = loadingAtendimento || loadingAgendamento

  useEffect(() => {
    if (!isLoading) {
      const agendadoSelecionado = cacheState?.desfechoVacinacao.desfechoAtendimento?.atendimento?.agendado
      if (agendadoSelecionado && !agendamentosDia?.find((agendamentos) => agendamentos.id === agendadoSelecionado)) {
        cacheState.desfechoVacinacao.desfechoAtendimento.atendimento.agendado = undefined
      }

      if (!initialStateLoaded) {
        const agendamentoChecked =
          atendimento.agendado?.lotacaoAgendada.id !== sessao.acesso.id ? atendimento.agendado?.id : undefined

        setCacheState(
          initialValue(
            prontuario?.puerpera,
            prontuario?.gestante,
            atendimento?.tiposServico,
            canAccess,
            agendamentosDia,
            agendamentoChecked
          )
        )
        setInitialStateLoaded(true)
      }
    }
  }, [
    initialStateLoaded,
    setCacheState,
    prontuario,
    isLoading,
    atendimento,
    canAccess,
    agendamentosDia,
    cacheState,
    sessao.acesso.id,
  ])

  const statusInvalido =
    atendimento?.statusAtendimento === StatusAtendimento.ATENDIMENTO_REALIZADO ||
    atendimento?.statusAtendimento === StatusAtendimento.NAO_AGUARDOU

  const tipoValido = atendimento?.atendimentoProfissional?.tipo === TipoAtendimentoProfissional.VACINACAO

  if (isLoading || !initialStateLoaded) {
    return (
      <>
        <Breadcrumb title='Atendimento de vacinação' />
        <PageLoading message='Carregando dados do atendimento de vacinação...' />
      </>
    )
  } else if (statusInvalido || !tipoValido) {
    return <Redirect to='/404' />
  } else if (atendimento?.atendimentoProfissional.lotacao.id !== acesso?.id) {
    return <Redirect to='/403' />
  }

  const renderVacinacaoView = () => {
    return (
      <VacinacaoView
        cacheState={cacheState}
        atendimentoId={atendimentoId}
        prontuarioId={prontuario?.id}
        cidadao={cidadao}
        dataAtendimento={atendimentoProfissional?.iniciadoEm}
        clearCache={deleteCacheState}
        updateCache={setCacheState}
        agendamentosDia={agendamentosDia}
        gestante={prontuario?.gestante}
        puerpera={prontuario?.puerpera}
        isUnidadeSaudeCEO={isUnidadeSaudeCEO}
        lastSaved={cacheState.lastSaved}
      />
    )
  }

  const renderCidadaoDetail = () => (
    <CidadaoDetailView
      atendimentoId={atendimentoId}
      cidadaoId={cidadao?.id}
      clearCache={deleteCacheState}
      tipoAtendimentoProfissional={atendimento.atendimentoProfissional.tipo}
      callbackUrl='lista-atendimento/atendimento-vacinacao'
    />
  )

  return (
    <>
      <Breadcrumb title='Atendimento de vacinação' />

      <AtendimentoHeader
        cidadaoId={cidadao?.id}
        renderFolhaRosto={false}
        renderSoap={false}
        renderHistorico={false}
        renderAntecedentes={false}
        renderVacinacao
      />
      <PecSwitch>
        <Redirect exact path={match.url} to={`${match.url}/vacinacao`} />
        <Route path={`${match.url}/vacinacao`} render={renderVacinacaoView} />
        <Route path={`${match.url}/cadastro-cidadao`} render={renderCidadaoDetail} />
      </PecSwitch>
    </>
  )
}
